import React, { useContext, useEffect, useState } from "react"
import { Form, notification, Spin, Card, Modal } from "antd"
import DF from "DisplayFramework"
import TitlePage from "components/theme_components/Title"
import WizardSteps from "components/theme_components/WizardSteps"
import { useNavigate } from "react-router-dom"
import { AuthContext } from "components/Auth/Auth"
import ConfirmModal from "components/theme_components/ThirdParty/ConfirmModal"
import moment from "moment"
import { toCurrency } from 'utils/utils'
import {
  type ConformityRequest,
  type ConformityResponse,
  processSupplierInvoiceWorkflow,
} from "services/supplierInvoice"
import { type ReservationData, type ProposalResponse, tpAuthorizationUseSFS } from "services/proposal"
import type { SupplierInvoiceResponse } from "services/fincontract"
import {
  getThirdPartyAuthorizationByTpreference,
  getThirdPartyById,
  getThirdPartyAuthorizationListByTpid,
} from "services/thirdParty"
import InvoiceInitialStep from "./steps/InvoiceInitialStep"
import InvoiceReservedStep from "./steps/InvoiceReservedStep"
import InvoiceUnreservedStep from "./steps/InvoiceUnreservedStep"
import InvoiceFinalStep from "./steps/InvoiceFinalStep"
import { useQuery } from "react-query"
import { getOfferDetailByOfferId } from "services/pricing"
import { updateStatusContract, type UpdateStatusContractParams, type ExecParametersRequest } from "services/workflow"
import ReadOnlyActivityCard from "pages/ThirdParty/ThirdPartySFS/ThirdPartyDetailSFS/components/Activity/ReadOnlyActivityCard"

type WorkflowMode = "initial" | "reserved" | "unreserved" | "final" | "spr"

type InvoiceWizardMainProps = {
  setVisible: (visible: boolean) => void
  data?: any
  initialData?: {
    codeClientFacture?: string
    [key: string]: any
  }
  onSave?: (conformityRequest: ConformityRequest, response: ConformityResponse) => void
  onError?: (error: any) => void
}

interface ClientActivityPanelProps {
  tpid: number | null
  familleFinancement: string
  tptype?: string
  tpbrand?: string
}

const ClientActivityPanel = ({ tpid, familleFinancement, tptype = "CONC", tpbrand = "" }: ClientActivityPanelProps) => {
  const { data, refetch, isFetching } = useQuery(
    ["client-activity", tpid, familleFinancement],
    async () => {
      const response = await getThirdPartyAuthorizationListByTpid({ tpid: Number(tpid) })
      return response.list || []
    },
    {
      enabled: !!tpid,
      refetchOnWindowFocus: false,
    },
  )

  const filteredAuthorizations = React.useMemo(() => {
    if (!data || !familleFinancement) return []

    return data
      .filter((auth) => auth.prcode === familleFinancement)
      .sort((a, b) => {
        const order = ["VN", "VD", "VO", "SPR"]
        const aIndex = order.indexOf(a.prcode ?? "")
        const bIndex = order.indexOf(b.prcode ?? "")
        return (aIndex === -1 ? order.length : aIndex) - (bIndex === -1 ? order.length : bIndex)
      })
  }, [data, familleFinancement])

  if (isFetching) {
    return (
      <div
        style={{
          textAlign: "center",
          padding: "60px 30px",
          background: "#f1f5f9",
          borderRadius: "8px",
          border: "2px dashed #94a3b8",
        }}
      >
        <Spin size="large" />
        <h4 style={{ marginTop: 20, color: "#475569", fontSize: "18px" }}>Chargement des autorisations...</h4>
        <p style={{ margin: "8px 0 0 0", color: "#64748b", fontSize: "15px" }}>
          Récupération des limites pour {familleFinancement}
        </p>
      </div>
    )
  }

  if (filteredAuthorizations.length === 0) {
    return (
      <div
        style={{
          textAlign: "center",
          padding: "60px 40px",
          background: "linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)",
          borderRadius: "12px",
          border: "2px solid #f59e0b",
          boxShadow: "0 4px 12px rgba(245, 158, 11, 0.15)",
        }}
      >
        <div style={{ fontSize: "56px", marginBottom: "20px" }}>⚠️</div>
        <h4
          style={{
            color: "#92400e",
            margin: "0 0 12px 0",
            fontSize: "20px",
            fontWeight: "600",
          }}
        >
          Aucune autorisation pour "{familleFinancement}"
        </h4>
        <p
          style={{
            margin: 0,
            fontSize: "16px",
            color: "#b45309",
            lineHeight: "1.5",
            maxWidth: "450px",
            marginLeft: "auto",
            marginRight: "auto",
          }}
        >
          Ce client n'a pas de limite d'autorisation configurée pour cette famille de financement.
          <br />
          <strong>Vérifiez la configuration du client avant de procéder.</strong>
        </p>
      </div>
    )
  }

  return (
    <div style={{ width: "100%" }}>
      {filteredAuthorizations.map((auth, index) => (
        <div
          key={`${auth.prcode}-${index}`}
          style={{
            marginBottom: "18px",
            width: "100%",
            maxWidth: "100%",
            overflow: "hidden",
          }}
        >
          <div
            className="activity-card-container"
            style={{
              width: "100%",
              transform: "none",
              transformOrigin: "unset",
            }}
          >
            <ReadOnlyActivityCard tptype={tptype} refetch={refetch} data={auth} />
          </div>
        </div>
      ))}
    </div>
  )
}

const InvoiceWizardMain: React.FC<InvoiceWizardMainProps> = ({ setVisible, data, initialData, onSave, onError }) => {

  const [form] = Form.useForm()
  const navigate = useNavigate()
  const { isInspect, userInfo } = useContext(AuthContext)

  const [current, setCurrent] = useState(0)
  const [workflowMode, setWorkflowMode] = useState<WorkflowMode>("initial")
  const [completedSteps, setCompletedSteps] = useState<number[]>([])
  const [isBlocked, setIsBlocked] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isCheckingVin, setIsCheckingVin] = useState(false)
  const [isCreatingReservation, setIsCreatingReservation] = useState(false)
  const [isLoadingAuth, setIsLoadingAuth] = useState(false)
  const [isLoadingInvoice, setIsLoadingInvoice] = useState(false)
  const [isValidatingContract, setIsValidatingContract] = useState(false)

  const [vinReserved, setVinReserved] = useState<boolean>(false)
  const [reservationData, setReservationData] = useState<ReservationData | null>(null)
  const [proposalResponse, setProposalResponse] = useState<ProposalResponse | null>(null)
  const [suggestedBrand, setSuggestedBrand] = useState<string>("")
  const [authBrand, setAuthBrand] = useState<string>("")
  const [isManuallySelected, setIsManuallySelected] = useState<boolean>(false)
  const [lastFetchKey, setLastFetchKey] = useState<string>("")
  const [isCreditNote, setIsCreditNote] = useState<boolean>(false)
  const [originalInvoiceData, setOriginalInvoiceData] = useState<SupplierInvoiceResponse | null>(null)

  const [clientTpid, setClientTpid] = useState<number | null>(null)
  const [clientTptype, setClientTptype] = useState<string>("")
  const [clientTpbrand, setClientTpbrand] = useState<string>("")

  const [modalVisible, setModalVisible] = useState(false)
  const [imageModalVisible, setImageModalVisible] = useState(false)
  const [selectedImage, setSelectedImage] = useState<string>("")
  const [refusedReservationModal, setRefusedReservationModal] = useState(false)
  const [refusedReservationData, setRefusedReservationData] = useState<any>(null)

  // Nouveaux états pour la vérification préalable des limites
  const [preValidationModal, setPreValidationModal] = useState(false)
  const [preValidationData, setPreValidationData] = useState<{
    requestedAmount: number;
    availableAmount: number;
    clientCode: string;
    familleFinancement: string;
  }>({
    requestedAmount: 0,
    availableAmount: 0,
    clientCode: '',
    familleFinancement: ''
  })

  const [authExpirationModal, setAuthExpirationModal] = useState(false)
  const [expiredAuthData, setExpiredAuthData] = useState<{
    familleFinancement: string;
    endDate: string;
    clientCode: string;
  }>({
    familleFinancement: '',
    endDate: '',
    clientCode: ''
  })
  const formatInvoiceNumberAsVin = (invoiceNumber: string): string => {
    if (!invoiceNumber) return "SPR".padEnd(17, 'X')

    if (invoiceNumber.length >= 17) {
      return invoiceNumber.substring(0, 17).toUpperCase()
    }
    return invoiceNumber.toUpperCase().padEnd(17, 'X')
  }

  const fetchClientInfo = async (codeClientFacture: string, familleFinancement: string) => {

    try {
      const authResponse = await getThirdPartyAuthorizationByTpreference({
        tpreference: codeClientFacture,
        prcode: familleFinancement,
      })

      if (authResponse.code === "200" && authResponse.data?.tpid) {
        const tpid = authResponse.data.tpid
        setClientTpid(tpid)
        const thirdPartyResponse = await getThirdPartyById({ tpid: tpid })
        if (thirdPartyResponse.code === "200" && thirdPartyResponse.data) {
          setClientTptype(thirdPartyResponse.data.generalData?.tptype || "CONC")
          setClientTpbrand(authResponse.data?.authbrand || thirdPartyResponse.data.generalData?.tpbrand || "")
        }

        if (authResponse.data.authDetail && authResponse.data.authDetail.length > 0) {
          const firstAuthDetail = authResponse.data.authDetail[0]

          if (firstAuthDetail.offerid) {
            try {

              const offerResponse = await getOfferDetailByOfferId({
                offerid: firstAuthDetail.offerid
              })

              if (offerResponse.code === "200" && offerResponse.data?.durationmax) {

                form.setFieldValue("dureeFranchise", offerResponse.data.durationmax)

                notification.info({
                  message: "Durée de franchise détectée",
                  description: `Durée de franchise automatiquement définie à ${offerResponse.data.durationmax} jours.`,
                  duration: 4,
                })
              } else {
                console.log(" Pas de durationmax dans la réponse")
              }
            } catch (error) {
              console.error(" Erreur lors de l'appel getOfferDetailByOfferId:", error)
            }
          } else {
            console.log("Pas d'offerid dans authDetail")
          }
        } else {
          console.log(" Pas d'authDetail dans la réponse")
        }
      }
    } catch (error) {
      console.error("❌ Erreur dans fetchClientInfo:", error)
      setClientTpid(null)
      setClientTptype("")
      setClientTpbrand("")
    }
  }
// Nouvelle fonction de vérification d'expiration des autorisations
  const checkAuthorizationExpiration = async (codeClientFacture: string, familleFinancement: string): Promise<boolean> => {
    try {
      // First get the authorization by preference to get the tpid
      const authResponse = await getThirdPartyAuthorizationByTpreference({
        tpreference: codeClientFacture,
        prcode: familleFinancement,
      })

      if (authResponse.code === "200" && authResponse.data?.tpid) {
        // Get the authorization list to check expiration
        const authListResponse = await getThirdPartyAuthorizationListByTpid({ 
          tpid: authResponse.data.tpid 
        })
        
        const currentAuth = authListResponse.list?.find(
          auth => auth.prcode === familleFinancement
        )

        if (currentAuth && currentAuth.enddate) {
          const currentDate = moment()
          const endDate = moment(currentAuth.enddate)
          
          // Check if authorization is expired (enddate <= current date)
          if (endDate.isSameOrBefore(currentDate, 'day')) {
            setExpiredAuthData({
              familleFinancement,
              endDate: endDate.format('DD/MM/YYYY'),
              clientCode: codeClientFacture
            })
            setAuthExpirationModal(true)
            return false
          }
        }
      }
      
      return true 
    } catch (error) {
      console.error("Error checking authorization expiration:", error)
      return true
    }
  }
  const codeClientFactureValue = Form.useWatch("codeClientFacture", form)
  const familleFinancementValue = Form.useWatch("familleFinancement", form)

  useEffect(() => {
    if (workflowMode === "unreserved" && codeClientFactureValue && familleFinancementValue) {
      fetchClientInfo(codeClientFactureValue, familleFinancementValue)
    } else {
      setClientTpid(null)
      setClientTptype("")
      setClientTpbrand("")
    }
  }, [codeClientFactureValue, familleFinancementValue, workflowMode])

  // Nouvelle fonction de vérification préalable des limites
  const checkLimitBeforeReservation = async (): Promise<boolean> => {
    try {
      const values = await form.validateFields([
        "codeClientFacture",
        "familleFinancement", 
        "VIN",
        "reservationAmount",
        "codeMarque",
        "cafNumber",
        "propNumber",
        "dateEmissionFacture"
      ])

      // Récupérer les informations d'autorisation en temps réel
      const authResponse = await getThirdPartyAuthorizationByTpreference({
        tpreference: values.codeClientFacture,
        prcode: values.familleFinancement,
      })

      if (authResponse.code === "200" && authResponse.data?.tpid) {
        // Récupérer la liste des autorisations pour avoir les montants à jour
        const authListResponse = await getThirdPartyAuthorizationListByTpid({ 
          tpid: authResponse.data.tpid 
        })
        
        const currentAuth = authListResponse.list?.find(
          auth => auth.prcode === values.familleFinancement
        )

        if (currentAuth && currentAuth.authavailableamount !== undefined) {
          const requestedAmount = parseFloat(values.reservationAmount)
          const availableAmount = currentAuth.authavailableamount

          // Si le montant demandé > reste disponible, demander confirmation
          if (requestedAmount > availableAmount) {
            setPreValidationData({
              requestedAmount,
              availableAmount,
              clientCode: values.codeClientFacture,
              familleFinancement: values.familleFinancement
            })
            setPreValidationModal(true)
            return false // Arrêter le processus
          }
        }
      }

      return true // Continuer le processus normal
    } catch (error) {
      console.error("Erreur lors de la vérification des limites:", error)
      notification.error({
        message: "Erreur de vérification",
        description: "Impossible de vérifier les limites disponibles",
        duration: 5,
      })
      return false
    }
  }

  // Nouvelle fonction de validation automatique
  const handleAutoValidation = async (reservationData: any) => {
    try {
      setIsValidatingContract(true)

      const ctrid = reservationData.ctrid || reservationData.entityId

      const contractUpdateParams: UpdateStatusContractParams = {
        Authorization: "",
        requestParam: {
          comment: "Manual validation - Amount exceeds limit but user confirmed",
          entity: "PROPOSAL", 
          entityId: ctrid,
          externalreference: "",
          gridid: 0,
          jobid: 0,
          pardate: moment().format("YYYY-MM-DD"),
          parvalue: "",
          phase: "",
          stage: "",
          status: "ACCEPMAN",
          taskid: reservationData.taskid || 1448,
          tpid: 0,
          tprole: "",
          currcode: "EUR",
          objectreference: "",
          requestedamount: form.getFieldValue("reservationAmount") || 0,
        }
      }

      const contractResponse = await updateStatusContract(contractUpdateParams)

      if (contractResponse.success) {
        setProposalResponse(reservationData)
        setCurrent(2)
        setCompletedSteps([0, 1])

        // Set form values
        const emissionDate = form.getFieldValue("dateEmissionFacture")
        form.setFieldsValue({
          dateEmissionFacture: moment(emissionDate),
          dateDebutCalculInteret: moment(emissionDate),
          montantTTC: form.getFieldValue("reservationAmount"),
          montantHTVehicule: form.getFieldValue("reservationAmount"),
          montantTVA: 0,
          montantFrais: 0,
          montantAutresTaxes: 0,
          dureeFranchise: reservationData.ctfranchiseperiod || 0,
          codePays: "FR",
          codeDevise: "EUR",
          typeVehicule: "1",
        })

        notification.success({
          message: "Réservation créée et validée automatiquement",
          description: `Référence: ${reservationData.propreference || reservationData.ctreference}. La réservation a été validée manuellement car elle dépasse la limite disponible.`,
          duration: 6,
        })
      } else {
        throw new Error(contractResponse.message || "Erreur lors de la validation automatique")
      }
    } catch (error: any) {
      console.error("Auto validation error:", error)
      notification.error({
        message: "Erreur de validation automatique",
        description: error.message || "Impossible de valider automatiquement la réservation.",
        duration: 5,
      })
    } finally {
      setIsValidatingContract(false)
    }
  }

  // Gestionnaires pour le modal de pré-validation
  const handlePreValidationConfirm = async () => {
    setPreValidationModal(false)
    // Créer la réservation en mode forcé (avec validation automatique si refusée)
    await createReservationFromNext(true)
  }

  const handlePreValidationCancel = () => {
    setPreValidationModal(false)
    notification.info({
      message: "Opération annulée",
      description: "La création de réservation a été annulée car le montant dépasse la limite disponible.",
      duration: 4,
    })
  }
// Gestionnaire pour le modal d'expiration d'autorisation
  const handleAuthExpirationConfirm = () => {
    setAuthExpirationModal(false)
    notification.warning({
      message: "Autorisation expirée",
      description: `L'autorisation pour la famille ${expiredAuthData.familleFinancement} a expiré le ${expiredAuthData.endDate}. Veuillez contacter l'administrateur pour renouveler l'autorisation.`,
      duration: 8,
    })
  }
  const getStepsConfig = () => {
    switch (workflowMode) {
      case "initial":
        return [
          { stepcomcode: "TypeSelection", key: "step1" },
          { stepcomcode: "Processing", key: "step_processing" }
        ]
      case "reserved":
        return [
          { stepcomcode: "TypeSelection", key: "step1" },
          { stepcomcode: "FinalInvoice", key: "step3" },
        ]
      case "unreserved":
        return [
          { stepcomcode: "TypeSelection", key: "step1" },
          { stepcomcode: "PreInvoicing", key: "step2" },
          { stepcomcode: "FinalInvoice", key: "step3" },
        ]
      case "spr":
        return [
          { stepcomcode: "TypeSelection", key: "step1" },
          { stepcomcode: "FinalInvoice", key: "step3" },
        ]
      default:
        return []
    }
  }

  const steps = getStepsConfig()

  const stepsContent = {
    step1: (
      <InvoiceInitialStep
        form={form}
        required={current === 0}
        isInspect={isInspect}
        onVinValidation={{
          isCheckingVin,
          setIsCheckingVin,
          setVinReserved,
          setReservationData,
          setWorkflowMode,
          setCurrent,
          setCompletedSteps,
          setIsLoadingAuth,
          setAuthBrand,
          setIsBlocked,
        }}
      />
    ),
    step_processing: <div>Processing...</div>,
    step2:
      workflowMode === "reserved" ? (
        <InvoiceReservedStep
          form={form}
          reservationData={reservationData}
          authBrand={authBrand}
          isLoadingAuth={isLoadingAuth}
          isInspect={isInspect}
        />
      ) : (
        <div
          style={{
            background: "white",
            borderRadius: "12px",
            padding: "24px",
            boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
            border: "1px solid #e5e7eb",
            width: "100%",
            minHeight: "600px",
          }}
        >
          <div style={{ display: "flex", gap: "32px", width: "100%" }}>
            <div style={{ flex: "0 0 580px", minWidth: "580px" }}>
              <InvoiceUnreservedStep
                form={form}
                isInspect={isInspect}
                suggestedBrand={suggestedBrand}
                setSuggestedBrand={setSuggestedBrand}
                isManuallySelected={isManuallySelected}
                setIsManuallySelected={setIsManuallySelected}
                isLoadingAuth={isLoadingAuth}
                setIsLoadingAuth={setIsLoadingAuth}
                lastFetchKey={lastFetchKey}
                setLastFetchKey={setLastFetchKey}
                onCreateReservation={{
                  isCreatingReservation,
                  setIsCreatingReservation,
                  setProposalResponse,
                  setCurrent,
                  setCompletedSteps,
                  userInfo,
                }}
              />
            </div>

            <div
              style={{
                flex: "1",
                maxWidth: "100%",
                overflow: "hidden",
              }}
            >
              <Card
                title={
                  <div>
                    <h3
                      style={{
                        margin: 0,
                        color: "#2f2c4834",
                        fontSize: "20px",
                        fontWeight: "600",
                      }}
                    >
                      Activité Client {familleFinancementValue ? `- ${familleFinancementValue}` : ""}
                    </h3>
                  </div>
                }
                style={{
                  height: "fit-content",
                  maxHeight: "700px",
                  boxShadow: "0 4px 12px rgba(22, 19, 51, 0.1)1)",
                  width: "100%",
                }}
                bodyStyle={{
                  padding: "20px",
                  maxHeight: "600px",
                  overflowY: "auto",
                  overflowX: "hidden",
                }}
              >
                <style jsx>{`
                  .activity-card-container {
                    width: 100% !important;
                    max-width: 100% !important;
                  }
                  
                  .activity-card-container .ant-card {
                    width: 100% !important;
                    max-width: 100% !important;
                    font-size: 13px !important;
                  }
                  
                  .activity-card-container .ant-card-body {
                    padding: 16px !important;
                  }
                  
                  .activity-card-container .ant-typography {
                    font-size: 12px !important;
                    line-height: 1.4 !important;
                    margin-bottom: 4px !important;
                  }
                  
                  .activity-card-container .ant-typography-title {
                    font-size: 14px !important;
                    margin-bottom: 8px !important;
                  }
                  
                  .activity-card-container .ant-statistic {
                    margin-bottom: 8px !important;
                  }
                  
                  .activity-card-container .ant-statistic-content {
                    font-size: 16px !important;
                  }
                  
                  .activity-card-container .ant-statistic-title {
                    font-size: 11px !important;
                    margin-bottom: 2px !important;
                  }
                  
                  .activity-card-container .ant-progress {
                    margin-bottom: 8px !important;
                  }
                  
                  .activity-card-container .ant-progress-text {
                    font-size: 12px !important;
                  }
                  
                  .activity-card-container .ant-row {
                    margin-bottom: 8px !important;
                  }
                  
                  .activity-card-container .ant-col {
                    padding: 4px !important;
                  }
                  
                  .activity-card-container .ant-divider {
                    margin: 8px 0 !important;
                  }
                  
                  .activity-card-container .ant-tag {
                    font-size: 11px !important;
                    padding: 2px 6px !important;
                    margin: 2px !important;
                  }
                `}</style>

                {clientTpid && familleFinancementValue ? (
                  <ClientActivityPanel
                    tpid={clientTpid}
                    familleFinancement={familleFinancementValue}
                    tptype={clientTptype}
                    tpbrand={clientTpbrand}
                  />
                ) : codeClientFactureValue && familleFinancementValue ? (
                  <div style={{ textAlign: "center", padding: "50px" }}>
                    <Spin size="large" />
                    <p style={{ marginTop: 16, color: "#666" }}>Récupération des informations client...</p>
                  </div>
                ) : (
                  <div
                    style={{
                      textAlign: "center",
                      padding: "60px 30px",
                      color: "#999",
                    }}
                  >
                    <div style={{ fontSize: "48px", marginBottom: "20px" }}>📊</div>
                    <h4 style={{ color: "#666", margin: "0 0 12px 0", fontSize: "18px" }}>Activité Client</h4>
                    <p style={{ margin: 0, fontSize: "15px", lineHeight: "1.5" }}>
                      Saisissez le code client et la famille de financement
                      <br />
                      pour afficher les limites d'autorisation correspondantes
                    </p>
                  </div>
                )}
              </Card>
            </div>
          </div>
        </div>
      ),
    step3: (
      <InvoiceFinalStep
        form={form}
        workflowMode={workflowMode}
        reservationData={reservationData}
        proposalResponse={proposalResponse}
        isCreditNote={isCreditNote}
        setIsCreditNote={setIsCreditNote}
        originalInvoiceData={originalInvoiceData}
        setOriginalInvoiceData={setOriginalInvoiceData}
        isInspect={isInspect}
        isLoadingInvoice={isLoadingInvoice}
        setIsLoadingInvoice={setIsLoadingInvoice}
      />
    ),
  }

  const next = async () => {
    try {
      if (current === 0) {
        const familleFinancement = form.getFieldValue("familleFinancement")
         const codeClientFacture = form.getFieldValue("codeClientFacture")

      if (!familleFinancement) {
          notification.error({
            message: "Sélection requise",
            description: "Vous devez sélectionner une famille de produit avant de continuer.",
            duration: 5,
            placement: "topRight"
          })
          window.scrollTo({ top: 0, behavior: 'smooth' })
          return
        }

        if (codeClientFacture && familleFinancement && workflowMode !== "spr") {
          const isAuthValid = await checkAuthorizationExpiration(codeClientFacture, familleFinancement)
          if (!isAuthValid) {
            return 
          }
        }
        if (workflowMode === "spr") {
          await form.validateFields(["familleFinancement"])
          setCurrent(1)
          setCompletedSteps([0])
        } else {
          await form.validateFields(["familleFinancement", "VIN"])

          if (workflowMode === "reserved") {
            setCurrent(1)
            setCompletedSteps([0])
          } else if (workflowMode === "unreserved") {
            setCurrent(1)
            setCompletedSteps([0])
          } else {
            setCurrent(1)
          }
        }
      } else if (workflowMode === "spr" && current === 1) {
        return
      } else if (workflowMode === "reserved" && current === 1) {
        return
      } else if (workflowMode === "unreserved" && current === 1) {
        // Appeler la nouvelle fonction avec vérification préalable
        await createReservationFromNext(false)
      } else if (workflowMode === "unreserved" && current === 2) {
        return
      }
    } catch (error) {
      console.log("Validation failed:", error)
      notification.warning({
        message: "Validation échouée",
        description: "Veuillez corriger les erreurs avant de continuer",
        duration: 4,
      })
    }
  }

  const createReservationFromNext = async (forceCreate: boolean = false) => {
    try {
      // Si ce n'est pas un forçage, vérifier les limites d'abord
      if (!forceCreate) {
        const canProceed = await checkLimitBeforeReservation()
        if (!canProceed) {
          return // Arrêter si la limite est dépassée (modal affiché)
        }
      }

      const values = await form.validateFields([
        "codeClientFacture",
        "familleFinancement",
        "VIN",
        "reservationAmount",
        "codeMarque",
        "cafNumber",
        "propNumber",
        "dateEmissionFacture"
      ])

      setIsCreatingReservation(true)

      let thirdPartyData = null
      let detectedBrand = values.codeMarque

      try {
        const authResponse = await getThirdPartyAuthorizationByTpreference({
          tpreference: values.codeClientFacture,
          prcode: values.familleFinancement,
        })

        if (authResponse.code === "200" && authResponse.data?.tpid) {
          const tpid = authResponse.data.tpid

          if (authResponse.data?.authbrand && !isManuallySelected) {
            detectedBrand = authResponse.data.authbrand
            form.setFieldValue("codeMarque", detectedBrand)
            form.setFieldValue("brandLabel", detectedBrand)
          }

          const thirdPartyResponse = await getThirdPartyById({ tpid: tpid })
          if (thirdPartyResponse.code === "200" && thirdPartyResponse.data) {
            thirdPartyData = thirdPartyResponse.data
          }
        }
      } catch (error) {
        console.warn("Could not fetch thirdparty data:", error)
      }
      const formattedDate = moment(values.dateEmissionFacture).format("YYYY-MM-DD")
      const requestData = {
        prcode: values.familleFinancement,
        tpreference: values.codeClientFacture,
        taxcode: thirdPartyData?.generalData?.tptaxid || "",
        fiscalid: thirdPartyData?.generalData?.tpfiscalid || "",
        financedamount: values.reservationAmount,
        mgid: userInfo?.mgid?.toString() || "1",
        movementcode: "SASK",
        requesteddate: formattedDate,
        vehserialnumber: values.VIN,
        priceht: values.reservationAmount,
        pricettc: values.reservationAmount,
        proporeference: values.VIN,
        constructorcode: values.codeMarque,
        treatmentOrder: 1,
        brand: values.codeMarque,
        cafNumber: values.cafNumber,
        startdate: formattedDate,
        rejectionReasonCode: "",
        descriptionOfRejection: "",
        paymentDelay: "",
        paymentDelayType: "",
        isTransformedVehicle: false,
      }

      const response = await tpAuthorizationUseSFS(requestData)

      if (response.code === "200" && response.data) {
        // Si c'était un forçage (dépassement de limite accepté), valider automatiquement
        if (forceCreate && response.data.ctstatus === "REFUSE") {
          await handleAutoValidation(response.data)
        } else if (response.data.ctstatus === "REFUSE") {
          setRefusedReservationData(response.data)
          setRefusedReservationModal(true)
          return
        } else {
          // Réservation acceptée normalement
          setProposalResponse(response.data)
          setCurrent(2)
          setCompletedSteps([0, 1])

          form.setFieldsValue({
            dateEmissionFacture: moment(values.dateEmissionFacture),
            dateDebutCalculInteret: moment(values.dateEmissionFacture),
            montantTTC: values.reservationAmount,
            montantHTVehicule: values.reservationAmount,
            montantTVA: 0,
            montantFrais: 0,
            montantAutresTaxes: 0,
            dureeFranchise: form.getFieldValue("dureeFranchise") || 0,
            codePays: "FR",
            codeDevise: "EUR",
            typeVehicule: "1",
          })

          notification.success({
            message: "Pré-facturation créée avec succès",
            description: `Référence: ${response.data.propreference || response.data.ctreference}. Vous pouvez maintenant créer la facture.`,
            duration: 6,
          })
        }
      } else {
        throw new Error(response.message || "Erreur lors de la création de la pré-facturation")
      }
    } catch (error: any) {
      notification.error({
        message: "Erreur de création",
        description: error.message || "Impossible de créer la pré-facturation",
        duration: 5,
      })
    } finally {
      setIsCreatingReservation(false)
    }
  }

  const handleManualValidation = async () => {
    if (!refusedReservationData) return

    try {
      setIsValidatingContract(true)

      const ctrid = refusedReservationData.ctrid || refusedReservationData.entityId

      if (!ctrid) {
        notification.error({
          message: "Erreur de validation",
          description: "Impossible de récupérer l'ID du contrat pour la validation",
          duration: 5,
        })
        return
      }

      const contractUpdateParams: UpdateStatusContractParams = {
        Authorization: "",
        requestParam: {
          comment: "Manual validation of refused reservation",
          entity: "PROPOSAL",
          entityId: ctrid,
          externalreference: "",
          gridid: 0,
          jobid: 0,
          pardate: moment().format("YYYY-MM-DD"),
          parvalue: "",
          phase: "",
          stage: "",
          status: "ACCEPMAN",
          taskid: refusedReservationData.taskid || 1448,
          tpid: 0,
          tprole: "",
          currcode: "EUR",
          objectreference: "",
          requestedamount: form.getFieldValue("reservationAmount") || 0,
        }
      }

      const contractResponse = await updateStatusContract(contractUpdateParams)

      if (contractResponse.success) {
        setProposalResponse(refusedReservationData)
        setCurrent(2)
        setCompletedSteps([0, 1])

        form.setFieldsValue({
          dateEmissionFacture: moment(),
          dateDebutCalculInteret: moment(),
          montantTTC: form.getFieldValue("reservationAmount"),
          montantHTVehicule: form.getFieldValue("reservationAmount"),
          montantTVA: 0,
          montantFrais: 0,
          montantAutresTaxes: 0,
          dureeFranchise: refusedReservationData.ctfranchiseperiod || 0,
          codePays: "FR",
          codeDevise: "EUR",
          typeVehicule: "1",
        })

        notification.success({
          message: "Réservation validée avec succès",
          description: `Référence: ${refusedReservationData.propreference || refusedReservationData.ctreference}. Le contrat a été validé manuellement et vous pouvez maintenant créer la facture.`,
          duration: 6,
        })

        setRefusedReservationModal(false)
        setRefusedReservationData(null)
      } else {
        throw new Error(contractResponse.message || "Erreur lors de la validation du contrat")
      }
    } catch (error: any) {
      console.error("Contract validation error:", error)
      notification.error({
        message: "Erreur de validation du contrat",
        description: error.message || "Impossible de valider le contrat. Veuillez réessayer.",
        duration: 5,
      })
    } finally {
      setIsValidatingContract(false)
    }
  }

  const handleManualCancel = () => {
    setRefusedReservationModal(false)
    setRefusedReservationData(null)
  }

  const prev = () => {
    if (current > 0) {
      setCurrent(current - 1)
    } else {
      resetToInitialState()
    }
  }

  const resetToInitialState = () => {
    setWorkflowMode("initial")
    setCurrent(0)
    setCompletedSteps([])
    setVinReserved(false)
    setReservationData(null)
    setProposalResponse(null)
    setIsCreditNote(false)
    setOriginalInvoiceData(null)
    setIsManuallySelected(false)
    setLastFetchKey("")
    setSuggestedBrand("")
    setAuthBrand("")
    setClientTpid(null)
    setClientTptype("")
    setClientTpbrand("")

    const currentFamille = form.getFieldValue("familleFinancement")
    const currentFamilyLabel = form.getFieldValue("familyLabel")

    form.resetFields()

    if (currentFamille) {
      form.setFieldsValue({
        familleFinancement: currentFamille,
        familyLabel: currentFamilyLabel,
      })
    }
  }

  const cancel = () => {
    setCurrent(0)
    form.resetFields()
    setModalVisible(true)
  }

  const handleSubmit = async () => {
    try {
      setIsSubmitting(true)

      if (workflowMode === "unreserved" && current === 2) {
        form.setFieldValue("dateFacture", undefined);
      }

      let values;
      try {
        let fieldsToValidate = [];

        if (workflowMode === "spr") {
          fieldsToValidate = [
            "numFacture",
            "dateEmissionFacture",
            "montantTTC",
            "montantHTVehicule",
            "codeClientFacture",
            "codeMarque"
          ];
        } else {
          fieldsToValidate = [
            "numFacture",
            "dateEmissionFacture",
            "montantTTC",
            "montantHTVehicule",
            "codeClientFacture",
            "VIN", 
            "dureeFranchise",
            "versionLabelLong",
            "motorisation",
            "versionYear",
            "co2EmissionWLTP"
          ];
        }

        values = await form.validateFields(fieldsToValidate);

        const allValues = form.getFieldsValue();
        values = { ...allValues, ...values };

      } catch (validationError: any) {
        const errorFieldNames = validationError.errorFields?.map((field: any) => field.name.join('.')) || [];

        notification.error({
          message: "Erreur de validation",
          description: `Veuillez corriger les champs: ${errorFieldNames.join(', ')}`,
          duration: 8,
        });

        return;
      }

      if (!values.dateEmissionFacture) {
        notification.error({
          message: "Date manquante",
          description: "Veuillez sélectionner une date d'émission",
          duration: 5,
        });
        return;
      }

      let finalVin = values.VIN;
      if (workflowMode === "spr") {
        if (!values.numFacture) {
          notification.error({
            message: "Numéro de facture manquant",
            description: "Le numéro de facture est requis pour les pièces de rechange",
            duration: 5,
          });
          return;
        }
        finalVin = formatInvoiceNumberAsVin(values.numFacture);
      }

      // FIX 1: Map SPR to PR for backend
      let finalFamilleFinancement = values.familleFinancement;
      if (workflowMode === "spr" && values.familleFinancement === "SPR") {
        finalFamilleFinancement = "PR";
      }

      const invoiceData = {
        isManualProcess: true,
        codeMovement: isCreditNote ? "CRNO" : "INVO",
        typeDocument: isCreditNote ? "04" : "01",
        codePays: values.codePays || "FR",
        codeMarque: values.codeMarque,
        codeClientFacture: values.codeClientFacture,
        familleFinancement: finalFamilleFinancement, // Use mapped value
        typeVehicule: values.typeVehicule || "1",
        vin: finalVin,
        blvd: values.blvd,
        numFacture: values.numFacture,
        numFactureOrigine: values.numFactureOrigine,
        dateEmissionFacture: values.dateEmissionFacture?.format?.("YYYY-MM-DD") || values.dateEmissionFacture,
        dateDebutCalculInteret: values.dateEmissionFacture?.format?.("YYYY-MM-DD") || values.dateEmissionFacture,
        dureeFranchise: values.dureeFranchise || 0,
        codeDevise: values.codeDevise || "EUR",
        montantHTVehicule: Number.parseFloat(values.montantHTVehicule || "0"),
        montantAutresTaxes: Number.parseFloat(values.montantAutresTaxes || "0"),
        montantTVA: Number.parseFloat(values.montantTVA || "0"),
        montantFrais: Number.parseFloat(values.montantFrais || "0"),
        montantTTC: Number.parseFloat(values.montantTTC || "0"),
        ctreference:
          proposalResponse?.ctreference ||
          proposalResponse?.propreference ||
          (reservationData as any)?.ctreference ||
          (reservationData as any)?.propreference ||
          null,
        brandLabel: values.codeMarque,
        familyLabel: values.familyLabel || form.getFieldValue("familyLabel"),
        versionLabelLocalLong: values.versionLabelLong,
        motorisation: values.motorisation,
        co2EmissionWLTP: (values.co2EmissionWLTP ?? 0).toString(),
        versionYear: values.versionYear,
        forwardedAuthUserId: userInfo?.userid?.toString(),
        forwardedAuthUsername: userInfo?.username,
        forwardedAuthMgid: userInfo?.mgid?.toString(),
      }

      // Rest of handleSubmit remains the same...
      notification.info({
        key: "processing",
        message: "Traitement en cours",
        description: "Traitement de votre demande...",
        duration: 0,
      })

      const response = await processSupplierInvoiceWorkflow({
        conformityRequest: invoiceData,
      })

      if (response.data) {
        const conformityResponse = response.data
        notification.destroy("processing")

        if (conformityResponse.status === "SUCCESS") {
          notification.success({
            message: "Succès",
            description: `${conformityResponse.message || (isCreditNote ? "Avoir créé avec succès!" : "Facture créée avec succès!")}`,
            duration: 5,
          })

          if (onSave) {
            onSave(invoiceData, conformityResponse)
          }

          setTimeout(() => {
            setVisible(false)
          }, 2000)
        } else {
          throw new Error(conformityResponse.message || "Erreur lors de la création")
        }
      } else {
        throw new Error("Réponse invalide du serveur")
      }
    } catch (error: any) {
      notification.destroy("processing")
      notification.error({
        message: "Erreur de création",
        description: error.message || "Impossible de créer la facture",
        duration: 5,
      })

      if (onError) {
        onError(error)
      }
    } finally {
      setIsSubmitting(false)
    }
  }
  useEffect(() => {
    if (initialData) {
      form.setFieldsValue(initialData)
    }
  }, [initialData, form])

  const handleImageClick = (imageUrl: string) => {
    setSelectedImage(imageUrl)
    setImageModalVisible(true)
  }
  const isRecapValue = (workflowMode === "reserved" && current === 1) ||
    (workflowMode === "unreserved" && current === 2) ||
    (workflowMode === "spr" && current === 1)

  return (
    <DF.Provider pagcode="invoicemanual">
      <Form layout="vertical" form={form} labelAlign="left" className="invoicemanual">
        <TitlePage
          level={2}
          comcode="ManualInvoiceWizard"
          bgColor="purple"
          noTitleOverlay
          onPrev={() => setVisible(false)}
        />

        <div
          style={{
            maxWidth: workflowMode === "unreserved" && current === 1 ? "98vw" : "1200px",
            width: "100%",
            margin: "0 auto",
            paddingLeft: 20,
            paddingRight: 20,
            marginTop: 20,
          }}
        >
          <WizardSteps
            className="my-custom-class invoicewizard"
            steps={steps}
            stepsContent={stepsContent}
            direction="horizontal"
            currentStep={current}
            onNext={next}
            onPrev={prev}
            nextComcode="Next"
            prevComcode={current === 0 ? "Cancel" : "Previous"}
            doneComcode="CreateInvoice"
            onCancel={cancel}
            onDone={handleSubmit}
            isLoading={isSubmitting}
            isRecap={isRecapValue}
            classNameProp="container-stepper"
            disableNext={isBlocked || isCheckingVin || isCreatingReservation || isLoadingInvoice} />
        </div>
      </Form>

      <ConfirmModal
        visible={modalVisible}
        status="error"
        message="Êtes-vous sûr de vouloir annuler ?"
        description="La création de cette facture sera abandonnée"
        onConfirm={() => {
          form.resetFields()
          setVisible(false)
          setModalVisible(false)
        }}
        onCancel={() => !isInspect && setModalVisible(false)}
        className="modalConfirmOrCancel"
      />
<ConfirmModal
  visible={refusedReservationModal}
  status="warning"
  message="Pré-facturation refusée - Validation manuelle requise"
  description={`La demande de pré-facturation a été refusée automatiquement. Souhaitez-vous valider manuellement cette réservation pour continuer ?`}
  onConfirm={handleManualValidation}
  onCancel={handleManualCancel}
  confirmLoading={isValidatingContract}
  Confirmcomcode="Valider manuellement"
  Cancelcomcode="Annuler"
  className="modalRefusedReservation"
/>

 <ConfirmModal
 visible={preValidationModal}
 status="warning"
 message="Dépassement de limite détecté"
 description={
   <div>
     <p><strong>Montant demandé:</strong> {toCurrency(preValidationData.requestedAmount)}</p>
     <p><strong>Reste disponible:</strong> {toCurrency(preValidationData.availableAmount)}</p>
     <p style={{ color: '#ff4d4f', fontWeight: 'bold' }}>
       Le montant demandé dépasse la limite disponible de {toCurrency(preValidationData.requestedAmount - preValidationData.availableAmount)}.
     </p>
     <p>Souhaitez-vous continuer malgré ce dépassement ? La réservation sera automatiquement validée manuellement.</p>
   </div>
 }
 onConfirm={handlePreValidationConfirm}
 onCancel={handlePreValidationCancel}
 confirmLoading={isCreatingReservation || isValidatingContract}
 Confirmcomcode="Continuer"
 Cancelcomcode="Annuler"
 className="modalPreValidation"
/>
 <ConfirmModal
       visible={authExpirationModal}
       status="error"
       message="Autorisation expirée"
       description={
         <div>
           <p><strong>Client:</strong> {expiredAuthData.clientCode}</p>
           <p><strong>Famille de financement:</strong> {expiredAuthData.familleFinancement}</p>
           <p><strong>Date d'expiration:</strong> {expiredAuthData.endDate}</p>
           <p style={{ color: '#ff4d4f', fontWeight: 'bold', marginTop: '16px' }}>
             Cette autorisation a expiré et ne peut plus être utilisée pour créer de nouvelles factures.
           </p>
         
         </div>
       }
       onConfirm={handleAuthExpirationConfirm}
       onCancel={handleAuthExpirationConfirm}
       Confirmcomcode="Compris"
       Cancelcomcode="Fermer"
       className="modalAuthExpiration"
     />
      <Modal
        open={imageModalVisible}
        onCancel={() => setImageModalVisible(false)}
        footer={null}
        width="90vw"
        style={{ top: 20 }}
        bodyStyle={{
          padding: 0,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "70vh",
        }}
        title="Aperçu de l'image"
      >
        {selectedImage && (
          <button
            type="button"
            onClick={() => setImageModalVisible(false)}
            style={{
              border: 'none',
              background: 'none',
              padding: 0,
              cursor: 'zoom-out',
            }}
            aria-label="Fermer l'aperçu de l'image"
          >
            <img
              src={selectedImage || "/placeholder.svg"}
              alt="Image agrandie"
              style={{
                maxWidth: "100%",
                maxHeight: "80vh",
                objectFit: "contain",
              }}
            />
          </button>
        )}
      </Modal>
    </DF.Provider>
  )
}

export default InvoiceWizardMain